/**
 * User Management Screen - Main Component
 * Main screen with list view, filters, search, and navigation to detail page
 * Following the pattern from RoleManagementPage.tsx
 */

import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useTheme } from '../../../theme/useTheme';
import {
  View,
  Text,
  ScrollView,
  TextInput,
  TouchableOpacity,
  TouchableWithoutFeedback,
  StyleSheet,
  Platform,
} from 'react-native';
import MaterialIcons from 'react-native-vector-icons/MaterialIcons';
import { useDispatch, useSelector } from 'react-redux';
import type { RootState } from '../../../store';
import AdminBreadcrumb from '../components/AdminBreadcrumb';
import AdminTag from '../components/AdminTag';
import AdminButton from '../components/AdminButton';
import { SearchIcon, CloseIcon } from '../components/AdminIcons';
import { AdminColors } from '../constants/adminColors';
import FontFamily from '../../../constants/fonts';
import { createApiClient, getLocalTimestamp } from '../../../api/apiClient';
import { useToast } from '../../../components/LhToastContainer';
import { extractRoleOptionsFromResponse, mapScopeTypeToRoleScope } from '../../TicketingPortal/components/SubmitTicketFormPanel';

// User Management Components
import UserTable from './UserManagement/UserTable';
import UserDetailPage from './UserManagement/UserDetailPage';
import BulkActionBar from './UserManagement/BulkActionBar';
import NewUserBanner from './UserManagement/NewUserBanner';
import TrainingModal from './UserManagement/TrainingModal';
import NewUsersModal from './UserManagement/NewUsersModal';

// Hooks and utilities
import { useUserManagementData, PAGE_SIZE_OPTIONS } from './UserManagement/hooks/useUserManagementData';
import { useUserManagementHandlers } from './UserManagement/hooks/useUserManagementHandlers';
import {
  toggleUserSelection,
  selectAllUsers,
  clearSelection,
  setFilters,
  clearFilters,
  setSortConfig,
  resetSortConfig,
} from '../../../store/slices/userManagementSlice';
import { TrainingType, UserManagementView, ACCESS_LEVELS } from './UserManagement/userManagementTypes';
import {
  filterAndSortUsers as _filterAndSortUsers,
  hasActiveFilters,
  countOverwrittenTrainingDates,
  getUniqueAccessLevels as _getUniqueAccessLevels,
} from './UserManagement/userManagementUtils';

// ─── Props ────────────────────────────────────────────────────────────────────
interface UserManagementScreenProps {
  onBack: () => void;
}

type LocationMappingPayload = {
  roleId: number;
  scopeType?: 'SITE' | 'MARKET' | 'REGION';
  locationId?: number;
  scopeRegion?: string;
  scopeMarket?: string;
};

const normalizeRoleNameForScope = (value: string) => value.trim().toLowerCase().replace(/[^a-z0-9]+/g, '');

// Resolves scope (full/region/market/site) from the live /admin/users/roles scopeType
// for the given department + role \u2014 no hardcoded per-role-name map.
const resolveRoleScopeType = async (
  department: string,
  roleName: string,
): Promise<'full' | 'region' | 'market' | 'site' | null> => {
  try {
    const api = createApiClient();
    const response = await api.get('/admin/users/roles', {
      params: { department },
      _skipLocationContext: true,
    } as any);
    const roles = extractRoleOptionsFromResponse(response.data);
    const normalized = normalizeRoleNameForScope(roleName);
    const match = roles.find((role) => normalizeRoleNameForScope(role.name) === normalized);
    return match ? mapScopeTypeToRoleScope(match.scopeType) : null;
  } catch {
    return null;
  }
};

const isSameText = (left: string, right: string) => left.trim().toLowerCase() === right.trim().toLowerCase();

const extractLocationRows = (response: any): Array<any> => {
  const root = response?.data ?? response;
  return Array.isArray(root)
    ? root
    : Array.isArray(root?.content)
    ? root.content
    : Array.isArray(root?.data?.content)
    ? root.data.content
    : Array.isArray(root?.data)
    ? root.data
    : [];
};

const normalizeRoleForCreatePayload = (roleName: string): string => {
  const normalized = roleName.trim().toLowerCase();
  // APIM/dev environments may still validate legacy role names.
  if (normalized === 'region leader/division' || normalized === 'region / division leader') {
    return 'Region Leader';
  }
  return roleName;
};

const buildLocationMappings = (
  scopeType: 'full' | 'region' | 'market' | 'site',
  selectedLocations: string[],
  roleId: number,
  appLocations: Array<any>,
): { mappings: LocationMappingPayload[]; error?: string } => {
  if (scopeType === 'full') {
    // Privileged role — Admin MS expects only { roleId }, no location scope
    return { mappings: [{ roleId }] };
  }

  const normalizedSelections = Array.from(
    new Set(selectedLocations.map((selection) => selection.trim()).filter(Boolean)),
  );

  if (!normalizedSelections.length) {
    return { mappings: [], error: 'Select at least one location for the chosen role scope.' };
  }

  if (scopeType === 'region') {
    return {
      mappings: normalizedSelections.map((region) => ({
        scopeType: 'REGION' as const,
        scopeRegion: region,
        roleId,
      })),
    };
  }

  if (scopeType === 'market') {
    const marketMappings: LocationMappingPayload[] = [];
    const seen = new Set<string>();

    for (const market of normalizedSelections) {
      const marketKey = market.trim().toLowerCase();

      const matchingRows = appLocations.filter((location: any) =>
        isSameText(String(location?.market ?? location?.marketName ?? ''), market),
      );

      const regions = Array.from(
        new Set(
          matchingRows
            .map((row: any) => String(row?.region ?? row?.regionName ?? '').trim())
            .filter(Boolean),
        ),
      );

      if (!regions.length) {
        // Fall back: send market without region — backend can infer it
        console.warn(`[UserManagement] Could not resolve region for market "${market}", sending without scopeRegion`);
        const key = `unknown::${marketKey}`;
        if (!seen.has(key)) {
          seen.add(key);
          marketMappings.push({ scopeType: 'MARKET' as const, scopeMarket: market, roleId });
        }
        continue;
      }

      regions.forEach((region) => {
        const key = `${region.toLowerCase()}::${marketKey}`;
        if (seen.has(key)) return;
        seen.add(key);
        marketMappings.push({
          scopeType: 'MARKET' as const,
          scopeRegion: region,
          scopeMarket: market,
          roleId,
        });
      });
    }

    return { mappings: marketMappings };
  }

  const siteMappings: LocationMappingPayload[] = [];
  const seenLocationIds = new Set<number>();

  for (const site of normalizedSelections) {
    const matchingRow = appLocations.find((location: any) => {
      const locationName = String(location?.name ?? location?.locName ?? location?.displayText ?? '').trim();
      return locationName && isSameText(locationName, site);
    });

    const locationIdValue = matchingRow?.locationId ?? matchingRow?.id;
    const numericLocationId = Number(locationIdValue);
    if (!Number.isFinite(numericLocationId)) {
      return { mappings: [], error: `Unable to resolve locationId for selected site "${site}".` };
    }

    if (seenLocationIds.has(numericLocationId)) continue;
    seenLocationIds.add(numericLocationId);

    siteMappings.push({
      scopeType: 'SITE' as const,
      locationId: numericLocationId,
      roleId,
    });
  }

  return { mappings: siteMappings };
};

// ─── Main Component ───────────────────────────────────────────────────────────
const UserManagementScreen: React.FC<UserManagementScreenProps> = ({ onBack }) => {
  const toast = useToast();

  // ── Data hook ──────────────────────────────────────────────────────────────
  const {
    users: allUsers,
    filteredUsers,
    selectedUserIds,
    filters,
    sortConfig,
    selectedUser,
    newUsersCount,
    activeUsersCount,
    inactiveUsersCount,
    isLoading: loading,
    error,
    currentPage,
    pageSize,
    totalCount,
    totalPages,
    goToPage,
    changePageSize,
    dispatch,
  } = useUserManagementData();

  // ── Handlers hook ──────────────────────────────────────────────────────────
  const {
    handleUpdateUser,
    handleBulkTrainingUpdate,
    handleClearNewTags,
    handleSelectUser,
    handleDeselectUser,
  } = useUserManagementHandlers({ dispatch });

  // Local state
  const [view, setView] = useState<UserManagementView>('list');
  const [showTrainingModal, setShowTrainingModal] = useState(false);
  const [currentTrainingType, setCurrentTrainingType] = useState<TrainingType | null>(null);
  const [showNewUsersModal, setShowNewUsersModal] = useState(false);
  const scrollViewRef = useRef<ScrollView>(null);

  // Local UI state for the rows-per-page dropdown
  const [pageSizeDropdownVisible, setPageSizeDropdownVisible] = useState(false);

  // Scroll to top when returning to list view
  useEffect(() => {
    if (view === 'list') {
      // Scroll to top on web
      if (Platform.OS === 'web') {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      } else {
        scrollViewRef.current?.scrollTo({ y: 0, animated: true });
      }
    }
  }, [view]);

  // Reset sort and filters when leaving the module
  useEffect(() => {
    return () => {
      dispatch(resetSortConfig());
      dispatch(clearFilters());
    };
  }, []);

  // Apply filters and sort — now handled server-side; no client-side
  // re-filtering needed. The useEffect below is intentionally removed.

  // Computed values
  const hasFilters = useMemo(() => hasActiveFilters(filters), [filters]);

  // Access level options: static list (covers all known levels including
  // Region Leader/Division which may not be in the current page).
  // Access level options: sourced from the roles fetched once in AppWrapper
  // (state.appData.userManagementRoles), falling back to the static list.
  const userManagementRoles = useSelector((state: RootState) => state.appData.userManagementRoles);
  const accessLevelOptions = useMemo(() => {
    const names = (userManagementRoles ?? [])
      .map((r: any) => r?.roleName ?? r?.RoleName ?? r?.name)
      .filter((n: any): n is string => typeof n === 'string' && n.trim().length > 0);
    if (names.length) {
      return Array.from(new Set(names)).sort();
    }
    const staticLevels = ACCESS_LEVELS as unknown as string[];
    return [...staticLevels].sort();
  }, [userManagementRoles]);

  // Location options: from the API master list (appData.locations).
  const appLocations = useSelector((state: RootState) => state.appData.locations);
  const locationOptions = useMemo(() => {
    const apiNames = appLocations
      .map(loc => loc.name)
      .filter((n): n is string => Boolean(n) && n !== 'ALL');
    const sorted = Array.from(new Set(apiNames)).sort();
    // 'ALL' is a static option — always pinned first, never duplicated
    return ['ALL', ...sorted];
  }, [appLocations]);

  // ─── Handlers ─────────────────────────────────────────────────────────────
  const handleSearchChange = (text: string) => {
    dispatch(setFilters({ search: text }));
  };

  const handleClearFilters = () => {
    dispatch(clearFilters());
  };

  const handleColumnFilterChange = (field: 'location' | 'accessLevel' | 'activeStatus', values: string[]) => {
    dispatch(setFilters({ [field]: values }));
  };

  const handleSort = (column: keyof typeof allUsers[0]) => {
    const newDirection = 
      sortConfig.column === column && sortConfig.direction === 'asc' 
        ? 'desc' 
        : 'asc';
    
    dispatch(setSortConfig({ column, direction: newDirection }));
  };

  const handleRowClick = (user: typeof allUsers[0]) => {
    handleSelectUser(user);
    setView('detail');
  };

  const handleBackToList = () => {
    handleDeselectUser();
    setView('list');
  };

  const handleClearNewTag = async (userId: string) => {
    const user = allUsers.find(u => u.id === userId);
    if (user) {
      await handleClearNewTags([user]);
    }
  };

  const handleNewBannerClick = () => {
    dispatch(setFilters({ newStatus: true }));
  };

  // ─── Computed values ──────────────────────────────────────────────────────
  const selectedUsers = useMemo(() => {
    return allUsers.filter(user => selectedUserIds.includes(user.id));
  }, [allUsers, selectedUserIds]);

  // ─── Bulk Actions ─────────────────────────────────────────────────────────
  const handleOpenTrainingModal = (trainingType: TrainingType) => {
    setCurrentTrainingType(trainingType);
    setShowTrainingModal(true);
  };

  const handleApplyTrainingDate = async (date: string | null) => {
    if (currentTrainingType && selectedUsers.length > 0) {
      const success = await handleBulkTrainingUpdate(
        selectedUsers,
        currentTrainingType,
        date
      );
      
      if (success) {
        setShowTrainingModal(false);
        setCurrentTrainingType(null);
      }
    } else {
      setShowTrainingModal(false);
      setCurrentTrainingType(null);
    }
  };

  const handleOpenNewUsersModal = () => {
    setShowNewUsersModal(true);
  };

  // Role IDs for privileged access types — bypasses the Normal User org/dept/role flow
  const PRIVILEGED_ROLE_IDS: Record<string, number> = {
    'Super User': 1,
    'Developer': 15,
  };

  const handleAddUserSubmit = async (payload: UserAddPayload): Promise<boolean> => {
    const email = payload.email.trim().toLowerCase();
    const gpid = payload.gpid.trim();

    if (!email || !gpid) {
      toast.showToast({
        header: 'Validation Error',
        subtext: 'Email and GPID are required.',
        variant: 'error',
        position: 'top',
      });
      return false;
    }

    // ── Super User / Developer — full access, no org/dept/location needed ──
    const isFullAccessType = payload.accessType === 'Super User' || payload.accessType === 'Developer';
    if (isFullAccessType) {
      const privilegedRoleId = PRIVILEGED_ROLE_IDS[payload.accessType];
      try {
        const api = createApiClient();

        // Step 1: Check email doesn't already exist
        try {
          const lookupRes = await api.get('/admin/users/by-email', {
            params: { email },
            _skipLocationContext: true,
          } as any);
          const lookupData = lookupRes.data?.data ?? lookupRes.data;
          const existingUserId = lookupData?.userId ?? lookupData?.id ?? lookupData?.user_id ?? null;
          if (existingUserId) {
            toast.showToast({
              header: 'User Already Exists',
              subtext: `A user with email "${email}" already exists in the system.`,
              variant: 'error',
              position: 'top',
            });
            return false;
          }
        } catch (lookupErr: any) {
          if (lookupErr?.response?.status !== 404) {
            const msg = lookupErr?.response?.data?.message ?? lookupErr?.message ?? 'User lookup failed.';
            toast.showToast({ header: 'Error', subtext: msg, variant: 'error', position: 'top' });
            return false;
          }
          // HTTP 404 → user does not exist → proceed to create
        }

        // Step 2: POST — Super User / Developer get full-access locationMappings (roleId only, no scope)
        const postBody: Record<string, unknown> = {
          userEmail: email,
          gpid,
          activeStatus: true,
          newStatus: false,
          role: payload.accessType,
          locationMappings: [{ roleId: privilegedRoleId }],
        };
        if (adminEmail) {
          postBody.approvedBy = adminEmail;
        }

        await api.post('/admin/users', postBody, {
          params: { clientTimestamp: getLocalTimestamp() },
          _skipLocationContext: true,
        } as any);

        await refreshUsers();
        toast.showToast({
          header: 'Success',
          subtext: `${payload.accessType} user created successfully.`,
          variant: 'success',
          position: 'top',
        });
        return true;
      } catch (error: any) {
        const message = error?.response?.data?.message ?? error?.message ?? 'Failed to create user.';
        toast.showToast({ header: 'Error', subtext: message, variant: 'error', position: 'top' });
        return false;
      }
    }

    // ── Normal User — requires roleId, scope resolution, and locationMappings ──
    const roleId = payload.roleId;
    if (roleId == null) {
      toast.showToast({
        header: 'Validation Error',
        subtext: `Admin roleId is required but could not be resolved for "${payload.role}".`,
        variant: 'error',
        position: 'top',
      });
      return false;
    }

    const scopeType = await resolveRoleScopeType(payload.department, payload.role);
    if (!scopeType) {
      toast.showToast({
        header: 'Validation Error',
        subtext: `Unable to resolve access scope for role "${payload.role}".`,
        variant: 'error',
        position: 'top',
      });
      return false;
    }

    let adminLocationRows: Array<any> = [];
    try {
      const api = createApiClient();
      const locationResponse = await api.get('/admin/locations', {
        params: { organizationType: payload.organization, page: 0, pageSize: 2000 },
        _skipLocationContext: true,
      } as any);
      adminLocationRows = extractLocationRows(locationResponse);
    } catch (error: any) {
      const message = error?.response?.data?.message ?? error?.message ?? 'Failed to load admin locations.';
      toast.showToast({ header: 'Error', subtext: message, variant: 'error', position: 'top' });
      return false;
    }

    const { mappings: locationMappings, error: mappingError } = buildLocationMappings(
      scopeType,
      payload.selectedLocations,
      roleId,
      adminLocationRows,
    );

    if (mappingError) {
      toast.showToast({ header: 'Validation Error', subtext: mappingError, variant: 'error', position: 'top' });
      return false;
    }

    try {
      const api = createApiClient();

      // Step 1: Check email doesn't already exist
      try {
        const lookupRes = await api.get('/admin/users/by-email', {
          params: { email },
          _skipLocationContext: true,
        } as any);
        const lookupData = lookupRes.data?.data ?? lookupRes.data;
        const existingUserId = lookupData?.userId ?? lookupData?.id ?? lookupData?.user_id ?? null;
        if (existingUserId) {
          toast.showToast({
            header: 'User Already Exists',
            subtext: `A user with email "${email}" already exists in the system.`,
            variant: 'error',
            position: 'top',
          });
          return false;
        }
      } catch (lookupErr: any) {
        if (lookupErr?.response?.status !== 404) {
          const msg = lookupErr?.response?.data?.message ?? lookupErr?.message ?? 'User lookup failed.';
          toast.showToast({ header: 'Error', subtext: msg, variant: 'error', position: 'top' });
          return false;
        }
        // HTTP 404 → user does not exist → proceed to create
      }

      // Step 2: POST — create normal user
      const createRoleName = normalizeRoleForCreatePayload(payload.role);
      const postBody: Record<string, unknown> = {
        userEmail: email,
        gpid,
        activeStatus: true,
        newStatus: false,
        organization: payload.organization.trim(),
        department: payload.department.trim(),
        role: createRoleName,
        locationMappings,
      };
      if (adminEmail) {
        postBody.approvedBy = adminEmail;
      }
      await api.post('/admin/users', postBody, {
        params: { clientTimestamp: getLocalTimestamp() },
        _skipLocationContext: true,
      } as any);

      await refreshUsers();
      toast.showToast({
        header: 'Success',
        subtext: 'User created successfully.',
        variant: 'success',
        position: 'top',
      });
      return true;
    } catch (error: any) {
      const message = error?.response?.data?.message ?? error?.message ?? 'Failed to create user.';
      toast.showToast({ header: 'Error', subtext: message, variant: 'error', position: 'top' });
      return false;
    }
  };

  const handleClearNewTagsModal = async () => {
    if (selectedUsers.length > 0) {
      const success = await handleClearNewTags(selectedUsers);
      
      if (success) {
        setShowNewUsersModal(false);
      }
    } else {
      setShowNewUsersModal(false);
    }
  };

  const overwriteCount = useMemo(() => {
    if (!currentTrainingType) return 0;
    const field = currentTrainingType === 'fundamentals' 
      ? 'fundamentals' 
      : currentTrainingType === 'lighthouse' 
      ? 'lighthouse' 
      : 'advFundamentals';
    return countOverwrittenTrainingDates(allUsers, selectedUserIds, field);
  }, [allUsers, selectedUserIds, currentTrainingType]);

  // ─── Render Detail View ───────────────────────────────────────────────────
  if (view === 'detail') {
    return (
      <View style={styles.container}>
        <AdminBreadcrumb
          items={[
            { label: 'Configuration Module', onPress: onBack },
            { label: 'User Management', onPress: handleBackToList },
            { label: selectedUser?.userEmail || 'User Details' },
          ]}
        />
        <UserDetailPage 
          onBack={handleBackToList} 
          onSave={handleUpdateUser}
        />
      </View>
    );
  }

  // ─── Render List View ─────────────────────────────────────────────────────
  return (
    <View style={styles.container}>
      {/* Breadcrumb */}
      <AdminBreadcrumb
        items={[
          { label: 'Configuration Module', onPress: onBack },
          { label: 'User Management' },
        ]}
      />

      {/* Page Header */}
      <View style={styles.header}>
        <View>
          <Text style={styles.title}>User Management</Text>
          <Text style={styles.subtitle}>
            {totalCount > 0
              ? `Showing ${currentPage * pageSize + 1}–${Math.min((currentPage + 1) * pageSize, totalCount)} of ${totalCount} user${totalCount !== 1 ? 's' : ''}`
              : filteredUsers.length > 0
              ? `${filteredUsers.length} user${filteredUsers.length !== 1 ? 's' : ''}`
              : 'No users'}
          </Text>
        </View>
      </View>

      {/* Error State */}
      {error && (
        <View style={styles.errorContainer}>
          <Text style={styles.errorTitle}>Error Loading Users</Text>
          <Text style={styles.errorText}>{error}</Text>
          <Text style={styles.errorHint}>
           We are facing issues. Please try again later.
          </Text>
        </View>
      )}

      {/* New User Banner */}
      {newUsersCount > 0 && (
        <NewUserBanner
          count={newUsersCount}
          onPress={handleNewBannerClick}
        />
      )}

      {/* Bulk Action Bar */}
      <BulkActionBar
        selectedCount={selectedUserIds.length}
        onFundamentals={() => handleOpenTrainingModal('fundamentals')}
        onLighthouse={() => handleOpenTrainingModal('lighthouse')}
        onAdvFundamentals={() => handleOpenTrainingModal('advFundamentals')}
        onNewUsers={handleOpenNewUsersModal}
        onClearSelection={() => dispatch(clearSelection())}
      />

      {/* Search Bar + Filters Row */}
      <View style={styles.searchBarWrapper}>
        <View style={styles.searchContainer}>
          <View style={styles.searchIcon}>
            <SearchIcon />
          </View>
          <TextInput
            style={styles.searchInput}
            placeholder="Search by User Email, GPID, Location, or Location ID"
            value={filters.search}
            onChangeText={handleSearchChange}
            placeholderTextColor={AdminColors.gray350}
          />
        </View>

        {/* Clear Filters */}
        {hasFilters && (
          <TouchableOpacity 
            style={styles.clearButton}
            onPress={handleClearFilters}
            activeOpacity={0.7}
          >
            <CloseIcon />
            <Text style={styles.clearButtonText}>Clear Filters</Text>
          </TouchableOpacity>
        )}
      </View>

      {/* Table Container */}
      <View style={styles.tableWrapper}>
        {/* Table */}
        <UserTable
          users={filteredUsers}
          selectedUserIds={selectedUserIds}
          sortConfig={sortConfig}
          onToggleSelection={(userId) => dispatch(toggleUserSelection(userId))}
          onSelectAll={() => {
            const allSelected = filteredUsers.length > 0 && selectedUserIds.length === filteredUsers.length;
            if (allSelected) {
              dispatch(clearSelection());
            } else {
              dispatch(selectAllUsers());
            }
          }}
          onSort={handleSort}
          onRowClick={handleRowClick}
          onClearNewTag={handleClearNewTag}
          onLoadMore={undefined}
          hasMore={false}
          isLoadingMore={false}
          searchQuery={filters.search}
          columnFilters={{
            location: filters.location,
            accessLevel: filters.accessLevel,
            activeStatus: filters.activeStatus,
          }}
          onColumnFilterChange={handleColumnFilterChange}
          locationOptions={locationOptions}
          accessLevelOptions={accessLevelOptions}
        />
      </View>

      {/* Pagination Controls – matches LocationTable design */}
      {totalPages >= 1 && (
        <TouchableWithoutFeedback onPress={() => setPageSizeDropdownVisible(false)}>
          <View style={styles.paginationRow}>

            {/* Left: Rows per page */}
            <View style={styles.rowsPerPageContainer}>
              <Text style={styles.rowsPerPageLabel}>Rows per page</Text>
              <View style={{ position: 'relative' }}>
                <TouchableOpacity
                  style={styles.pageSizeDropdown}
                  onPress={() => setPageSizeDropdownVisible(v => !v)}
                  activeOpacity={0.8}
                >
                  <Text style={styles.pageSizeDropdownText}>{pageSize}</Text>
                  <MaterialIcons
                    name={pageSizeDropdownVisible ? 'keyboard-arrow-up' : 'keyboard-arrow-down'}
                    size={20}
                    color="#616161"
                  />
                </TouchableOpacity>
                {pageSizeDropdownVisible && (
                  <View style={styles.pageSizeOptions}>
                    {PAGE_SIZE_OPTIONS.map(opt => (
                      <TouchableOpacity
                        key={opt}
                        style={[styles.pageSizeOption, opt === pageSize && styles.pageSizeOptionSelected]}
                        onPress={() => { changePageSize(opt); setPageSizeDropdownVisible(false); }}
                        activeOpacity={0.7}
                      >
                        <Text style={[styles.pageSizeOptionText, opt === pageSize && styles.pageSizeOptionTextSelected]}>
                          {opt}
                        </Text>
                      </TouchableOpacity>
                    ))}
                  </View>
                )}
              </View>
            </View>

            {/* Right: Page navigation */}
            <View style={styles.pageNavContainer}>
              {/* First */}
              <TouchableOpacity
                disabled={currentPage === 0}
                onPress={() => goToPage(0)}
                style={styles.navBtn}
                activeOpacity={0.7}
              >
                <MaterialIcons name="first-page" size={20} color={currentPage === 0 ? '#D0D0D0' : '#034895'} />
              </TouchableOpacity>

              {/* Prev */}
              <TouchableOpacity
                disabled={currentPage === 0}
                onPress={() => goToPage(currentPage - 1)}
                style={styles.navBtn}
                activeOpacity={0.7}
              >
                <MaterialIcons name="keyboard-arrow-left" size={20} color={currentPage === 0 ? '#D0D0D0' : '#034895'} />
              </TouchableOpacity>

              {/* Page indicator */}
              <View style={styles.pageIndicator}>
                <Text style={styles.pageText}>Page</Text>
                <View style={styles.currentPageBox}>
                  <Text style={styles.currentPageText}>{currentPage + 1}</Text>
                </View>
                <Text style={styles.pageOfText}>
                  of <Text style={styles.totalPagesText}>{totalPages}</Text>
                </Text>
              </View>

              {/* Next */}
              <TouchableOpacity
                disabled={currentPage >= totalPages - 1}
                onPress={() => goToPage(currentPage + 1)}
                style={styles.navBtn}
                activeOpacity={0.7}
              >
                <MaterialIcons name="keyboard-arrow-right" size={20} color={currentPage >= totalPages - 1 ? '#D0D0D0' : '#034895'} />
              </TouchableOpacity>

              {/* Last */}
              <TouchableOpacity
                disabled={currentPage >= totalPages - 1}
                onPress={() => goToPage(totalPages - 1)}
                style={styles.navBtn}
                activeOpacity={0.7}
              >
                <MaterialIcons name="last-page" size={20} color={currentPage >= totalPages - 1 ? '#D0D0D0' : '#034895'} />
              </TouchableOpacity>
            </View>

          </View>
        </TouchableWithoutFeedback>
      )}

      {/* Training Modal */}
      <TrainingModal
        visible={showTrainingModal}
        trainingType={currentTrainingType}
        selectedCount={selectedUserIds.length}
        overwriteCount={overwriteCount}
        onClose={() => {
          setShowTrainingModal(false);
          setCurrentTrainingType(null);
        }}
        onApply={handleApplyTrainingDate}
      />

      {/* New Users Modal */}
      <NewUsersModal
        visible={showNewUsersModal}
        selectedCount={selectedUserIds.length}
        onClose={() => setShowNewUsersModal(false)}
        onConfirm={handleClearNewTagsModal}
      />
    </View>
  );
};

// ─── Styles ───────────────────────────────────────────────────────────────────
const styles = StyleSheet.create({
  container: {
    flex: 1,
    padding: 24,
    backgroundColor: AdminColors.bgPage,
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 24,
    marginTop: 8,
  },
  title: {
    fontSize: 28,
    fontWeight: '700',
    lineHeight: 40,
    color: AdminColors.textPrimary,
    fontFamily: FontFamily.bold,
  },
  subtitle: {
    fontSize: 14,
    lineHeight: 20,
    color: AdminColors.textSecondary,
    fontFamily: FontFamily.regular,
    marginTop: 4,
  },
  loadingContainer: {
    padding: 40,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: AdminColors.bgSurface,
    borderRadius: 8,
    marginBottom: 24,
  },
  loadingText: {
    fontSize: 16,
    color: AdminColors.textSecondary,
    fontFamily: FontFamily.regular,
  },
  errorContainer: {
    padding: 24,
    backgroundColor: '#FEF2F2',
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#FCA5A5',
    marginBottom: 24,
  },
  errorTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: '#991B1B',
    fontFamily: FontFamily.bold,
    marginBottom: 8,
  },
  errorText: {
    fontSize: 14,
    color: '#DC2626',
    fontFamily: FontFamily.regular,
    marginBottom: 12,
  },
  errorHint: {
    fontSize: 13,
    color: '#7F1D1D',
    fontFamily: FontFamily.regular,
    lineHeight: 20,
  },
  searchBarWrapper: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    marginBottom: 12,
    flexWrap: 'wrap',
    zIndex: 20,
  },
  tableWrapper: {
    backgroundColor: AdminColors.bgSurface,
    borderRadius: 8,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.06,
    shadowRadius: 6,
    elevation: 2,
  },
  searchContainer: {
    position: 'relative',
    flex: 1,
    minWidth: 340,
  },
  searchIcon: {
    position: 'absolute',
    left: 8,
    top: 7,
    zIndex: 1,
  },
  searchInput: {
    height: 30,
    paddingLeft: 28,
    paddingRight: 12,
    paddingVertical: 4,
    borderWidth: 1,
    borderColor: AdminColors.blue700,
    borderRadius: 6,
    fontSize: 12,
    lineHeight: 16,
    color: AdminColors.textPrimary,
    fontFamily: FontFamily.regular,
    backgroundColor: AdminColors.bgSurface,
  },
  clearButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    paddingHorizontal: 10,
    paddingVertical: 3,
    height: 24,
    borderRadius: 12,
    backgroundColor: AdminColors.blue25,
    borderWidth: 1,
    borderColor: AdminColors.blue200,
  },
  clearButtonText: {
    fontSize: 11,
    lineHeight: 14,
    fontWeight: '600',
    color: AdminColors.blue700,
    fontFamily: FontFamily.bold,
  },
  // ─── Pagination ────────────────────────────────────────────────────────────
  paginationRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 16,
    paddingVertical: 12,
    backgroundColor: AdminColors.bgSurface,
    borderTopWidth: 1,
    borderTopColor: '#D7E5F8',
    flexWrap: 'wrap',
    gap: 8,
  },
  // Rows-per-page (left side)
  rowsPerPageContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  rowsPerPageLabel: {
    fontSize: 12,
    color: '#616161',
    fontFamily: FontFamily.regular,
  },
  pageSizeDropdown: {
    flexDirection: 'row',
    alignItems: 'center',
    borderWidth: 1,
    borderColor: '#D0D0D0',
    borderRadius: 4,
    paddingHorizontal: 8,
    paddingVertical: 4,
    gap: 4,
    backgroundColor: AdminColors.bgSurface,
    minWidth: 64,
  },
  pageSizeDropdownText: {
    fontSize: 12,
    color: '#3A3A3A',
    fontFamily: FontFamily.regular,
  },
  pageSizeOptions: {
    position: 'absolute',
    bottom: 36,
    left: 0,
    backgroundColor: AdminColors.bgSurface,
    borderWidth: 1,
    borderColor: '#D0D0D0',
    borderRadius: 4,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 8,
    elevation: 8,
    zIndex: 100,
    minWidth: 64,
  },
  pageSizeOption: {
    paddingHorizontal: 12,
    paddingVertical: 8,
  },
  pageSizeOptionSelected: {
    backgroundColor: '#EEF2F8',
  },
  pageSizeOptionText: {
    fontSize: 12,
    color: '#3A3A3A',
    fontFamily: FontFamily.regular,
  },
  pageSizeOptionTextSelected: {
    color: '#034895',
    fontFamily: FontFamily.medium,
    fontWeight: '600',
  },
  // Navigation (right side)
  pageNavContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 2,
  },
  navBtn: {
    padding: 4,
  },
  pageIndicator: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    marginHorizontal: 4,
  },
  pageText: {
    fontSize: 12,
    color: '#616161',
    fontFamily: FontFamily.regular,
  },
  currentPageBox: {
    borderWidth: 1,
    borderColor: '#D0D0D0',
    borderRadius: 4,
    paddingHorizontal: 8,
    paddingVertical: 2,
    minWidth: 28,
    alignItems: 'center',
  },
  currentPageText: {
    fontSize: 12,
    color: '#3A3A3A',
    fontFamily: FontFamily.medium,
    fontWeight: '600',
  },
  pageOfText: {
    fontSize: 12,
    color: '#616161',
    fontFamily: FontFamily.regular,
  },
  totalPagesText: {
    fontFamily: FontFamily.medium,
    fontWeight: '600',
    color: '#3A3A3A',
  },
});

export default UserManagementScreen;
